import { fabric } from 'fabric';
import BaseHandler from './BaseHandler';
import { objectTypes, SCALE_FACTOR } from '../common/constants';
import {
  stackIdOf,
  buildStack,
  withStack,
  withoutStack,
  flowOrder,
  groupByStack,
  mergedStackSettings,
  validateStack,
  memberStacks,
  memberMinFontSize,
  shrinksWithStack,
  memberBoundary,
  boundaryError,
  boundaryLines,
} from 'helpers/stacks';
import { shrinksToBox } from 'helpers/textBox';

/**
 * The on-canvas indicator colour. Deliberately not the selection purple, so a
 * stack outline is never mistaken for a selection.
 */
const INDICATOR_COLOR = '#e8590c';
const CHIP_FONT = '600 11px sans-serif';
const CHIP_HEIGHT = 16;
const CHIP_PADDING = 5;
const CHIP_RADIUS = 4;
const BOUNDARY_DASH = [8, 4];
/** How close to a boundary line, in screen px, a press picks it up. */
const LINE_HIT = 5;
/** How far below its designed bottom a new boundary goes, in design px. */
const NEW_BOUNDARY_GAP = 20;

/**
 * Push-down stack membership on the canvas.
 *
 * A stack is a set of objects on one page sharing `metadata.stack.id`; the
 * stitcher pushes the lower members down when a text above them wraps to more
 * lines than designed. This handler answers which objects are in which stack,
 * writes membership and settings, and draws the indicator. The rules for the
 * metadata itself live in helpers/stacks.
 *
 * Every mutation writes the same full stack value onto every member, so no
 * member is special and deleting one never loses the settings, with
 * shrink_to_fit derived from the members' own shrink choices (see
 * writeStack). Metadata is always replaced, never edited in place: duplicate
 * hands the copy the original's metadata object, so an in-place edit would
 * change both.
 */
class StacksHandler extends BaseHandler {
  constructor(props) {
    super(props);
    this.canvas.on('after:render', this.drawIndicators);
    this.canvas.on('mouse:up', this.onMouseUp);
    this.canvas.on('mouse:move', this.onHover);
    // capture phase on the wrapper, so a press on a boundary line is handled
    // before fabric's own mousedown on the upper canvas (see onLinePress)
    this.canvas.wrapperEl?.addEventListener(
      'mousedown',
      this.onLinePress,
      true
    );
  }

  /** The chips drawn on the last render, in screen px, for hit testing. */
  chips = [];

  /** Whether an object can be a stack member: a real template var. */
  isEligible = object =>
    Boolean(object) &&
    object.type !== objectTypes.FRAME &&
    object.type !== objectTypes.BACKGROUND_IMAGE &&
    object.type !== 'activeSelection' &&
    object.type !== 'group' &&
    object.name !== 'bgGrid';

  objects = () => this.canvas.getObjects().filter(this.isEligible);

  /**
   * The page an object sits on: the index of the nearest frame. A single-sided
   * template has one frame, so everything is on page 0.
   */
  pageIndexOf = (object, frames = this.handlers.frameHandler.getFrames()) => {
    if (frames.length <= 1) return 0;

    // the bounding box rather than getCenterPoint: inside a multi-selection an
    // object's left/top are relative to the selection, the box is not.
    const box = this.boundingRect(object, true);
    const center = {
      x: box.left + box.width / 2,
      y: box.top + box.height / 2,
    };
    let nearest = 0;
    let nearestDistance = Infinity;
    frames.forEach((frame, index) => {
      const dx = frame.left + frame.width / 2 - center.x;
      const dy = frame.top + frame.height / 2 - center.y;
      const distance = dx * dx + dy * dy;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });
    return nearest;
  };

  objectsOnPage = pageIndex => {
    const frames = this.handlers.frameHandler.getFrames();
    return this.objects().filter(
      object => this.pageIndexOf(object, frames) === pageIndex
    );
  };

  /** Map of stack id to members in flow order, for one page. */
  stacksOnPage = pageIndex => {
    const groups = groupByStack(this.objectsOnPage(pageIndex));
    const ordered = new Map();
    for (const [id, members] of groups) {
      ordered.set(id, flowOrder(members, this.flowTopOf));
    }
    return ordered;
  };

  stackIdsOnPage = pageIndex => [...this.stacksOnPage(pageIndex).keys()];

  /** The members of the object's stack in flow order, or [] when it has none. */
  membersOf = object => {
    const id = stackIdOf(object);
    if (id === null) return [];
    return this.stacksOnPage(this.pageIndexOf(object)).get(id) || [];
  };

  settingsOf = object => mergedStackSettings(this.membersOf(object));

  /**
   * The height the stitcher treats as designed for a text member, in template
   * pixels: its box height when the height is fixed (see helpers/textBox),
   * else the height of whatever text is on the canvas now. Longer rendered
   * text pushes the members below down by the difference.
   */
  designedHeight = object =>
    Math.round((object.height * object.scaleY) / SCALE_FACTOR);

  /**
   * Makes a new stack of the objects. They must share a page, and the id must
   * be unused there; use addToStack to join an existing one. Objects already
   * in another stack leave it.
   */
  createStack = (objects, id, settings = {}) => {
    const members = (objects || []).filter(this.isEligible);
    if (members.length === 0) {
      return { error: 'Select at least one object' };
    }

    const pages = new Set(members.map(member => this.pageIndexOf(member)));
    if (pages.size > 1) {
      return { error: 'Stack members must be on the same page' };
    }
    const [page] = pages;

    const name = String(id ?? '').trim();
    if (this.stackIdsOnPage(page).includes(name)) {
      return {
        error: `A stack named "${name}" already exists on this page`,
      };
    }

    // nothing shrinks with a new stack until a text chooses to
    const written = this.writeStack(name, members, settings, () => ({
      shrinks: false,
    }));
    if (!written.ok) return written;
    this.commit('stack:created');
    return { ok: true };
  };

  /** Adds the objects to an existing stack on their page, taking its settings. */
  addToStack = (objects, id) => {
    const members = (objects || []).filter(this.isEligible);
    if (members.length === 0) {
      return { error: 'Select at least one object' };
    }

    const pages = new Set(members.map(member => this.pageIndexOf(member)));
    if (pages.size > 1) {
      return { error: 'Stack members must be on the same page' };
    }
    const [page] = pages;

    const existing = this.stacksOnPage(page).get(id);
    if (!existing) {
      return { error: `There is no stack named "${id}" on this page` };
    }

    // the newcomers do not shrink with it until they choose to
    const joining = new Set(members);
    const written = this.writeStack(
      id,
      [...existing.filter(member => !joining.has(member)), ...members],
      mergedStackSettings(existing),
      member =>
        joining.has(member) ? { shrinks: false } : this.choiceOf(member)
    );
    if (!written.ok) return written;
    this.commit('stack:joined');
    return { ok: true };
  };

  /** Takes the objects out of whatever stack they are in. The stack stays. */
  removeFromStack = objects => {
    const members = (objects || []).filter(
      object => this.isEligible(object) && stackIdOf(object) !== null
    );
    if (members.length === 0) return { ok: true };

    // the stacks they leave, so the rest can be rewritten: shrink_to_fit may
    // have depended on a member that left
    const left = new Map();
    members.forEach(member => {
      const page = this.pageIndexOf(member);
      left.set(`${page}:${stackIdOf(member)}`, { page, id: stackIdOf(member) });
    });
    members.forEach(member =>
      this.write(member, withoutStack(member.metadata))
    );
    for (const { page, id } of left.values()) {
      const rest = this.stacksOnPage(page).get(id) || [];
      if (rest.length) {
        this.writeStack(id, rest, mergedStackSettings(rest), this.choiceOf);
      }
    }
    this.commit('stack:left');
    return { ok: true };
  };

  /** Removes the object's whole stack: every member on the page leaves it. */
  dissolveStack = object => {
    const members = this.membersOf(object);
    if (members.length === 0) return { ok: true };

    members.forEach(member =>
      this.write(member, withoutStack(member.metadata))
    );
    this.commit('stack:dissolved');
    return { ok: true };
  };

  /**
   * Rewrites the settings of the object's stack (mode and margins) onto every
   * member. Each member keeps its own shrink choice.
   */
  updateSettings = (object, settings) => {
    const id = stackIdOf(object);
    if (id === null) return { error: 'The object is not in a stack' };

    const written = this.writeStack(
      id,
      this.membersOf(object),
      settings,
      this.choiceOf
    );
    if (!written.ok) return written;
    this.commit('stack:updated');
    return { ok: true };
  };

  /**
   * Whether the object's text shrinks when its stack runs out of room, and
   * its floor. The stack's shrink_to_fit follows. Pass `commit: false` when
   * the caller saves the history step itself.
   */
  setMemberShrink = (object, shrinks, minFontSize, { commit = true } = {}) => {
    const id = stackIdOf(object);
    if (id === null) return { error: 'The object is not in a stack' };

    const written = this.writeStack(
      id,
      this.membersOf(object),
      this.settingsOf(object),
      member =>
        member === object
          ? { ...this.choiceOf(member), shrinks, minFontSize }
          : this.choiceOf(member)
    );
    if (!written.ok) return written;
    if (commit) this.commit('stack:updated');
    return { ok: true };
  };

  /** Whether a member can shrink at all: only text grows, so only text shrinks. */
  isText = object => object instanceof fabric.Textbox;

  /**
   * A member's own keys as they are now: its shrink choice (a text that
   * shrinks to its own box never grows, so it never shrinks with the stack),
   * its floor and its boundary.
   */
  choiceOf = member => ({
    shrinks:
      this.isText(member) && shrinksWithStack(member) && !shrinksToBox(member),
    minFontSize: memberMinFontSize(member),
    boundary: memberBoundary(member),
  });

  /** The frame (page) an object sits on. */
  frameOf = object => {
    const frames = this.handlers.frameHandler.getFrames();
    return frames[this.pageIndexOf(object, frames)] || null;
  };

  /** A canvas y in design px from the top of the object's page. */
  toDesignY = (object, canvasY) =>
    (canvasY - (this.frameOf(object)?.top || 0)) / SCALE_FACTOR;

  /** Design px from the top of the object's page as a canvas y. */
  toCanvasY = (object, designY) =>
    (this.frameOf(object)?.top || 0) + designY * SCALE_FACTOR;

  /** The member's bottom as designed, in design px: a boundary may not be above it. */
  designedBottom = object => {
    const box = this.boundingRect(object, true);
    return Math.round(this.toDesignY(object, box.top + box.height));
  };

  /** The boundary lines of the object's stack, top to bottom. */
  boundariesOf = object => boundaryLines(this.membersOf(object));

  /**
   * Gives the members a boundary at design y `y`, or takes theirs away when
   * `y` is null. All of them must be in the same stack, and a boundary may
   * not be above any of their designed bottoms.
   */
  setBoundary = (members, y) => {
    const list = (members || []).filter(m => stackIdOf(m) !== null);
    if (list.length === 0) return { error: 'Select a stack member' };
    const id = stackIdOf(list[0]);
    const page = this.pageIndexOf(list[0]);
    if (list.some(m => stackIdOf(m) !== id || this.pageIndexOf(m) !== page)) {
      return { error: 'The members are not in one stack' };
    }
    if (y !== null) {
      for (const member of list) {
        const message = boundaryError(y, this.designedBottom(member));
        if (message) return { errors: { boundary: message } };
      }
    }

    const moving = new Set(list);
    const all = this.membersOf(list[0]);
    const written = this.writeStack(id, all, mergedStackSettings(all), member =>
      moving.has(member)
        ? { ...this.choiceOf(member), boundary: y }
        : this.choiceOf(member)
    );
    if (!written.ok) return written;
    this.commit('stack:boundary');
    return { ok: true };
  };

  /** Gives the member a boundary just below its designed bottom. */
  addBoundary = member =>
    this.setBoundary([member], this.designedBottom(member) + NEW_BOUNDARY_GAP);

  /**
   * Writes the stack onto its members: the shared settings, shrink_to_fit on
   * exactly when some text member shrinks with the stack, and each member's
   * own choice from `choiceOf(member)`. Validates everything before writing
   * anything.
   */
  writeStack = (id, members, settings, choiceOf) => {
    const stacks = memberStacks(id, members, settings, choiceOf, this.isText);
    for (const stack of stacks.values()) {
      const errors = validateStack(stack);
      if (Object.keys(errors).length) return { errors };
    }
    members.forEach(member =>
      this.write(member, withStack(member.metadata, stacks.get(member)))
    );
    return { ok: true };
  };

  /** Gives the object's stack a new id on every member. */
  renameStack = (object, newId) => {
    const id = stackIdOf(object);
    if (id === null) return { error: 'The object is not in a stack' };

    const name = String(newId ?? '').trim();
    const errors = validateStack(buildStack(name, this.settingsOf(object)));
    if (Object.keys(errors).length) return { errors };
    if (name === id) return { ok: true };

    if (this.stackIdsOnPage(this.pageIndexOf(object)).includes(name)) {
      return {
        error: `A stack named "${name}" already exists on this page`,
      };
    }

    const written = this.writeStack(
      name,
      this.membersOf(object),
      this.settingsOf(object),
      this.choiceOf
    );
    if (!written.ok) return written;
    this.commit('stack:renamed');
    return { ok: true };
  };

  /** Selects a member on the canvas, e.g. from the member list. */
  /**
   * A click on a stack's chip selects every member of that stack, so the
   * whole stack can be moved, deleted or edited at once. Mouse up rather than
   * down: fabric has finished its own selection handling by then.
   */
  onMouseUp = opt => {
    if (opt.isClick === false || !opt.e) return;
    const pointer = this.canvas.getPointer(opt.e, true);
    const chip = this.chips.find(
      c =>
        pointer.x >= c.left &&
        pointer.x <= c.left + c.width &&
        pointer.y >= c.top &&
        pointer.y <= c.top + c.height
    );
    if (!chip) return;

    this.selectMembers(this.stacksOnPage(chip.page).get(chip.id) || []);
  };

  /** Selects every member of the object's stack. */
  selectStack = object => this.selectMembers(this.membersOf(object));

  selectMembers = members => {
    if (members.length === 0) return;

    this.canvas.discardActiveObject();
    const selection =
      members.length === 1
        ? members[0]
        : new fabric.ActiveSelection(members, { canvas: this.canvas });
    // setActiveObject fires the selection events, which the events handler
    // turns into the editor's active object
    this.canvas.setActiveObject(selection);
    this.canvas.requestRenderAll();
  };

  select = object => {
    this.canvas.discardActiveObject();
    this.canvas.setActiveObject(object);
    this.context.setActiveObject(object);
    this.canvas.requestRenderAll();
  };

  /**
   * The top of an object's bounding box in canvas space, ignoring the
   * viewport, so flow order is right for rotated and centre-origin objects.
   */
  flowTopOf = object => this.boundingRect(object, true).top;

  /**
   * The object's axis-aligned box: in canvas space when absolute, else on
   * screen (through the viewport transform). Built from the full transform
   * matrix rather than getBoundingRect, because in fabric 4 an object inside
   * a multi-selection reports left/top relative to the selection, which put
   * the chips off to the side whenever a stack was selected.
   */
  boundingRect = (object, absolute) => {
    const own = object.calcTransformMatrix();
    const matrix = absolute
      ? own
      : fabric.util.multiplyTransformMatrices(
          this.canvas.viewportTransform,
          own
        );
    const w = object.width / 2;
    const h = object.height / 2;
    const corners = [
      [-w, -h],
      [w, -h],
      [w, h],
      [-w, h],
    ].map(([x, y]) =>
      fabric.util.transformPoint(new fabric.Point(x, y), matrix)
    );
    return fabric.util.makeBoundingBoxFromPoints(corners);
  };

  write = (object, metadata) => {
    object.set('metadata', metadata);
  };

  commit = type => {
    this.canvas.requestRenderAll();
    this.handlers.historyHandler.save(type);
    this.editor.emit('stacks:changed', { type });
  };

  /**
   * Draws, over the canvas, a chip on every stack member naming its stack and
   * its place in the flow, and a dashed outline around the members of the
   * selected object's stack. Drawn on the selection context the way the
   * alignment guidelines are, so it survives fabric's own renders.
   */
  drawIndicators = () => {
    this.chips = [];
    this.lines = [];

    // only the stacks the selection belongs to: a selected member, or a
    // multi-selection holding members (e.g. after clicking a chip)
    const active = this.canvas.getActiveObject();
    if (!active) return;
    const selected = (active._objects || [active]).filter(
      object => this.isEligible(object) && stackIdOf(object) !== null
    );
    if (selected.length === 0) return;

    const frames = this.handlers.frameHandler.getFrames();
    const wanted = new Map();
    for (const object of selected) {
      const page = this.pageIndexOf(object, frames);
      if (!wanted.has(page)) wanted.set(page, new Set());
      wanted.get(page).add(stackIdOf(object));
    }

    const ctx = this.canvas.getSelectionContext();
    ctx.save();
    for (const [page, ids] of wanted) {
      const stacks = this.stacksOnPage(page);
      for (const id of ids) {
        this.drawBoundaries(ctx, frames[page], page, id, stacks.get(id) || []);
        (stacks.get(id) || []).forEach((member, index) => {
          const rect = this.boundingRect(member, false);
          this.drawOutline(ctx, rect);
          this.chips.push({
            ...this.drawChip(ctx, rect, `${id} · ${index + 1}`),
            page,
            id,
          });
        });
      }
    }
    ctx.restore();
  };

  /**
   * The boundary lines drawn on the last render, in screen px, for hit
   * testing: { page, id, y, members, x0, x1, screenY }.
   */
  lines = [];

  /**
   * Draws each distinct boundary of a stack as a dashed line across its page,
   * tagged with its design y and how many members use it.
   */
  drawBoundaries = (ctx, frame, page, id, members) => {
    if (!frame) return;
    for (const line of boundaryLines(members)) {
      // the line being dragged is drawn where the pointer has it
      const dragging =
        this.drag &&
        this.drag.line.page === page &&
        this.drag.line.id === id &&
        this.drag.line.y === line.y
          ? this.drag
          : null;
      const y = dragging ? dragging.y : line.y;
      const vpt = this.canvas.viewportTransform;
      const canvasY = frame.top + y * SCALE_FACTOR;
      const [a, b] = [frame.left, frame.left + frame.width].map(x =>
        fabric.util.transformPoint(new fabric.Point(x, canvasY), vpt)
      );
      ctx.save();
      ctx.strokeStyle = INDICATOR_COLOR;
      ctx.lineWidth = 1.5;
      ctx.setLineDash(BOUNDARY_DASH);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.restore();

      const count = line.members.length;
      const label =
        `${Math.round(y)}${dragging?.clamped ? ' (highest it can go)' : ''}` +
        ` · ${count} member${count === 1 ? '' : 's'}`;
      this.drawTag(ctx, b.x, a.y, label);
      this.lines.push({
        page,
        id,
        y: line.y,
        members: line.members,
        x0: a.x,
        x1: b.x,
        screenY: a.y,
      });
    }
  };

  /** The boundary line under a pointer in screen px, if any. */
  lineAt = pointer =>
    this.lines.find(
      line =>
        Math.abs(pointer.y - line.screenY) <= LINE_HIT &&
        pointer.x >= line.x0 - LINE_HIT &&
        pointer.x <= line.x1 + LINE_HIT
    ) || null;

  /** A line being dragged: { line, y, floor, clamped }, y in design px. */
  drag = null;

  onHover = opt => {
    if (this.drag || !opt.e || this.lines.length === 0) return;
    if (this.lineAt(this.canvas.getPointer(opt.e, true))) {
      this.canvas.setCursor('ns-resize');
    }
  };

  /**
   * A press on a boundary line starts dragging it. Stopping the event here,
   * before it reaches the upper canvas, keeps fabric from starting an object
   * drag or a drag-to-select, and the selection (which is what shows the
   * lines) stays as it is.
   */
  onLinePress = e => {
    if (e.button !== 0 || this.lines.length === 0) return;
    const line = this.lineAt(this.canvas.getPointer(e, true));
    if (!line) return;
    e.stopPropagation();
    e.preventDefault();
    this.drag = {
      line,
      y: line.y,
      // the line cannot go above any of its members' designed bottoms
      floor: Math.max(...line.members.map(this.designedBottom)),
      clamped: false,
    };
    window.addEventListener('mousemove', this.onLineDrag);
    window.addEventListener('mouseup', this.onLineDrop);
    this.canvas.setCursor('ns-resize');
  };

  onLineDrag = e => {
    if (!this.drag) return;
    const member = this.drag.line.members[0];
    const y = Math.round(this.toDesignY(member, this.canvas.getPointer(e).y));
    this.drag.y = Math.max(this.drag.floor, y);
    this.drag.clamped = y < this.drag.floor;
    this.canvas.setCursor('ns-resize');
    this.canvas.requestRenderAll();
  };

  /** Writes the new line onto every member that had the old one: one history step. */
  onLineDrop = () => {
    window.removeEventListener('mousemove', this.onLineDrag);
    window.removeEventListener('mouseup', this.onLineDrop);
    if (!this.drag) return;
    const { line, y } = this.drag;
    this.drag = null;
    if (y !== line.y) this.setBoundary(line.members, y);
    else this.canvas.requestRenderAll();
  };

  /** A small label hanging under the right end of a line. */
  drawTag = (ctx, right, y, label) => {
    ctx.save();
    ctx.font = CHIP_FONT;
    ctx.textBaseline = 'middle';
    const width = ctx.measureText(label).width + CHIP_PADDING * 2;
    ctx.fillStyle = INDICATOR_COLOR;
    ctx.fillRect(right - width, y + 1, width, CHIP_HEIGHT);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(
      label,
      right - width + CHIP_PADDING,
      y + 1 + CHIP_HEIGHT / 2 + 0.5
    );
    ctx.restore();
  };

  drawOutline = (ctx, rect) => {
    ctx.save();
    ctx.strokeStyle = INDICATOR_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(
      rect.left - 2.5,
      rect.top - 2.5,
      rect.width + 5,
      rect.height + 5
    );
    ctx.restore();
  };

  drawChip = (ctx, rect, label) => {
    ctx.save();
    ctx.font = CHIP_FONT;
    ctx.textBaseline = 'middle';
    const width = ctx.measureText(label).width + CHIP_PADDING * 2;
    // above the object when there is room, else tucked inside its top edge
    const top =
      rect.top - CHIP_HEIGHT - 3 >= 0
        ? rect.top - CHIP_HEIGHT - 3
        : rect.top + 3;
    const left = Math.max(0, rect.left);

    ctx.fillStyle = INDICATOR_COLOR;
    // rounded at the top, square where it meets the object below
    const r = Math.min(CHIP_RADIUS, width / 2);
    ctx.beginPath();
    ctx.moveTo(left, top + CHIP_HEIGHT);
    ctx.lineTo(left, top + r);
    ctx.arcTo(left, top, left + r, top, r);
    ctx.lineTo(left + width - r, top);
    ctx.arcTo(left + width, top, left + width, top + r, r);
    ctx.lineTo(left + width, top + CHIP_HEIGHT);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, left + CHIP_PADDING, top + CHIP_HEIGHT / 2 + 0.5);
    ctx.restore();
    return { left, top, width, height: CHIP_HEIGHT };
  };
}

export default StacksHandler;
